import { useTranslation } from 'react-i18next'
import { Check, Languages } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LANGUAGES, type LanguageCode } from '@/lib/constants'
import { cn } from '@/lib/utils'

/** Language picker. Shows the current language so it's discoverable (the old one was a bare globe icon). */
export default function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation('common')
  const current = (i18n.resolvedLanguage ?? 'he') as LanguageCode

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        aria-label={t('language')}
        className={cn(
          'inline-flex h-10 items-center gap-1.5 rounded-xl px-2.5 text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent',
          className
        )}
      >
        <Languages className="h-4 w-4" />
        <span>{LANGUAGES[current]?.short ?? current}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="min-w-40 rounded-xl p-1.5">
        {(Object.keys(LANGUAGES) as LanguageCode[]).map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => i18n.changeLanguage(code)}
            className="cursor-pointer justify-between gap-6 rounded-lg"
          >
            <span lang={code}>{LANGUAGES[code].name}</span>
            {code === current && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Inline segmented version for the mobile drawer. */
export function LanguageSegmented() {
  const { i18n } = useTranslation()
  const current = i18n.resolvedLanguage ?? 'he'
  return (
    <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted p-1">
      {(Object.keys(LANGUAGES) as LanguageCode[]).map((code) => (
        <button
          key={code}
          type="button"
          lang={code}
          onClick={() => i18n.changeLanguage(code)}
          className={cn(
            'rounded-lg py-2 text-sm font-medium transition-colors',
            code === current ? 'bg-card shadow-sm' : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {LANGUAGES[code].name}
        </button>
      ))}
    </div>
  )
}
